var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import * as client from 'openid-client';
import { useRef } from 'react';
/*
 * TODO: https://lucidlink.atlassian.net/browse/LUCID-20912
 * Return a token revocation function from this hook.
 *
 * Aws docs:
 * https://docs.aws.amazon.com/cognito/latest/developerguide/revocation-endpoint.html
 */
export const useSSOClient = () => {
    _react_refresh_temp_1();
    const scope = 'openid';
    const state = useRef(null);
    const codeVerifier = useRef(null);
    const config = useRef(null);
    const redirectUrl = useRef(null);
    const nonce = useRef(null);
    /*
     * From the openid-client js doc:
     * Marked as deprecated only to make it stand out as something you
     * shouldn't have the need to use, possibly only for local development and
     * testing against non-TLS secured environments.
     *
     * We are using it in dev/local so that we can make the http request
     * to the service.
     * */
    const executeOnDiscovery = process.env.ALLOW_INSECURE_OPEN_ID_REQUESTS === 'true'
        ? [client.allowInsecureRequests]
        : [];
    const initializeAuthentication = async ({ additionalAuthorizationUrlParams = {}, clientId, loginHint, redirectUri, serverUri, }) => {
        config.current = await client.discovery(new URL(`${serverUri}/.well-known/openid-configuration`), clientId, undefined, undefined, {
            execute: executeOnDiscovery,
        });
        state.current = client.randomState();
        codeVerifier.current = client.randomPKCECodeVerifier();
        // Seems like this nonce is somehow needed for google auth in LL Classic to work properly
        // We send it first when redirecting, then when calling
        // authorizationCodeGrant we need it again to verify it
        nonce.current = client.randomNonce();
        redirectUrl.current = redirectUri;
        const codeChallenge = await client.calculatePKCECodeChallenge(codeVerifier.current);
        const redirectTo = client.buildAuthorizationUrl(config.current, {
            redirect_uri: redirectUrl.current,
            scope,
            // Seems like this nonce is somehow needed for google auth?
            // We send it first when redirecting, then when calling authorizationCodeGrant
            // we need it again to verify
            nonce: nonce.current,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state: state.current,
            login_hint: loginHint,
            ...additionalAuthorizationUrlParams,
        });
        // NB!!! This below will fail with Amazon Cognito redirect_mismatch error
        // if the redirect URL is not added to the list of allowed callback URLs
        // in the Cognito User Pool App Client settings.
        // Make sure to add it there before testing SSO login.
        // Current list of added callback URLs in Cognito for dev/local testing:
        // http://localhost:4000/sso-classic/code
        // http://localhost:4001/sso-classic/code
        // http://localhost:4002/sso-classic/code
        // http://localhost:4003/sso-classic/code
        // https://webportal-be.lucidlink.com/webportal/redirect
        // http://localhost:3000/sso-landing
        // https://app.staging.lucidlink.com/sso-landing
        // https://app.lucidlink.dev/sso-landing
        // Current allowed callback URLs in Cognito for prod are:
        // http://localhost:4000/sso-classic/code
        // http://localhost:4001/sso-classic/code
        // http://localhost:4002/sso-classic/code
        // http://localhost:4003/sso-classic/code
        // https://auth.lucidlink.com/oauth2/idpresponse
        // https://www.lucidlink.com/webportal/redirect
        // https://app.lucidlink.com/sso-landing
        // To add / remove callback URLs in Cognito, contact DevOps team.
        // Also, window.open can be blocked by browsers if not triggered by a user action,
        // so make sure to call this function as a result of a user interaction (e.g., button click).
        // If you are testing locally and the redirect URL is using http,
        // you might also need to allow insecure localhost callback URLs
        // in your browser settings for the SSO flow to work properly.
        window.open(redirectTo, '_blank')?.focus();
    };
    const exchangeCode = async (uri) => {
        if (!codeVerifier.current ||
            !config.current ||
            !state.current ||
            !nonce.current) {
            return;
        }
        const url = new URL(`${redirectUrl.current}${uri}`);
        const tokens = await client.authorizationCodeGrant(config.current, url, {
            pkceCodeVerifier: codeVerifier.current,
            expectedState: state.current,
            expectedNonce: nonce.current,
        });
        return tokens;
    };
    const refreshTokens = async (refreshToken) => {
        if (!config.current) {
            return;
        }
        const tokens = await client.refreshTokenGrant(config.current, refreshToken);
        return tokens;
    };
    return {
        initializeAuthentication,
        exchangeCode,
        refreshTokens,
    };
};
_react_refresh_temp_1(useSSOClient, "RitmVLes80ZsY0qcLi9ndsBT0CY=");
